import { useState, useEffect, useCallback } from 'react';
import {
  Stamp, Check, X, Clock, RefreshCw, User, ChevronDown, ChevronUp,
  Mail, Send, MessageSquare, Calendar, AlertTriangle
} from 'lucide-react';

const API = getApiUrl();
import getApiUrl from '../../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const ACTION_META = {
  send_email: { label: "Email", icon: Mail, color: "text-cyan-400" },
  send_sms: { label: "SMS", icon: Send, color: "text-green-400" },
  send_whatsapp: { label: "WhatsApp", icon: MessageSquare, color: "text-emerald-400" },
  create_rdv: { label: "RDV", icon: Calendar, color: "text-blue-400" },
  modify_status: { label: "Statut", icon: RefreshCw, color: "text-amber-400" },
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function VisaIAWidget({ authUser }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [acting, setActing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/api/visa-ia/pending`);
      if (r.ok) setPending((await r.json()).actions || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  const approve = async (visaId) => {
    setActing(visaId);
    try {
      const r = await authFetch(`${API}/api/visa-ia/${visaId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewer: authUser?.email || "admin" })
      });
      if (r.ok) load();
    } catch (e) { console.error(e); }
    setActing(null);
  };

  const reject = async (visaId) => {
    setActing(visaId);
    try {
      const r = await authFetch(`${API}/api/visa-ia/${visaId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewer: authUser?.email || "admin", reason: "" })
      });
      if (r.ok) load();
    } catch (e) { console.error(e); }
    setActing(null);
  };

  return (
    <div className="bg-slate-900/60 rounded-xl border border-purple-500/20 overflow-hidden" data-testid="visa-ia-widget">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-800/40 transition-colors"
        data-testid="visa-ia-widget-toggle"
      >
        <div className="flex items-center gap-2">
          <Stamp className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-white">Visa IA</span>
          {pending.length > 0 && (
            <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[10px] font-bold bg-purple-500 text-white rounded-full">
              {pending.length}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-700/50">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-4 h-4 text-purple-400 animate-spin" />
            </div>
          )}

          {!loading && pending.length === 0 && (
            <div className="py-4 px-3 text-center">
              <p className="text-xs text-slate-500">Aucune action en attente</p>
            </div>
          )}

          {!loading && pending.length > 0 && (
            <div className="divide-y divide-slate-800/50 max-h-[300px] overflow-y-auto">
              {pending.map(action => {
                const meta = ACTION_META[action.action_type] || { label: action.action_type, icon: AlertTriangle, color: "text-slate-400" };
                const ActionIcon = meta.icon;
                const showDetail = detailId === action.visa_id;

                return (
                  <div key={action.visa_id} className="px-3 py-2.5" data-testid={`visa-widget-item-${action.visa_id}`}>
                    <div className="flex items-center gap-2">
                      <ActionIcon className={`w-3.5 h-3.5 flex-shrink-0 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-white truncate">{meta.label}</span>
                          {action.target && <span className="text-[10px] text-slate-500 truncate">→ {action.target}</span>}
                        </div>
                        <div className="text-[10px] text-slate-500">{action.agent_name} · {formatDate(action.created_at)}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          data-testid={`widget-approve-${action.visa_id}`}
                          onClick={() => approve(action.visa_id)}
                          disabled={acting === action.visa_id}
                          className="p-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded text-white"
                          title="Approuver"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                        <button
                          data-testid={`widget-reject-${action.visa_id}`}
                          onClick={() => reject(action.visa_id)}
                          disabled={acting === action.visa_id}
                          className="p-1 bg-red-600/80 hover:bg-red-500 disabled:opacity-50 rounded text-white"
                          title="Rejeter"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => setDetailId(showDetail ? null : action.visa_id)}
                          className="p-1 hover:bg-slate-700 rounded"
                        >
                          <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${showDetail ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </div>
                    {showDetail && action.context && (
                      <p className="mt-1.5 text-[10px] text-slate-400 bg-slate-800/50 rounded px-2 py-1">{action.context}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
